import { useState } from 'react';
import { Banner, Button, Dialog, useToast, type ButtonSize } from '../../../ds/components';
import { errorMessage } from '../../../lib/api';
import { invitePortal, useInvalidatePortal, type StaffPortalCard } from '../portal-api';
import { Badge } from './bits';
import styles from './portal.module.css';

export interface PortalInviteActionProps {
  contactId: string;
  contactName: string;
  /** The contact's e-mail of the record head, named in the confirmation. */
  email: string | null;
  card: StaffPortalCard;
  size?: ButtonSize | undefined;
}

/**
 * The deliberate invitation to the client portal (D-334): the account is created when the contact becomes a
 * customer, nothing is sent until the advisor confirms here. „Portal einladen" (no account or not invited yet),
 * „Einladung erneut senden" (invited, no password yet), „Portal aktiv" (status only). Locked accounts are handled
 * by the „Portalstatus" card („Zugang entsperren").
 */
export function PortalInviteAction({
  contactId,
  contactName,
  email,
  card,
  size = 'small',
}: PortalInviteActionProps) {
  const { toast } = useToast();
  const invalidate = useInvalidatePortal();
  const [confirm, setConfirm] = useState(false);
  const [busy, setBusy] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);
  const state = card.status.state;

  if (state === 'active') return <Badge tone="success">Portal aktiv</Badge>;
  if (state === 'locked') return null;
  const label = state === 'invited' ? 'Einladung erneut senden' : 'Portal einladen';

  async function send() {
    setBusy(true);
    setFailure(null);
    try {
      await invitePortal(contactId);
      setConfirm(false);
      toast({ message: 'Der Zugang ist unterwegs.' });
      await invalidate(contactId);
    } catch (cause) {
      setFailure(errorMessage(cause));
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <Button
        variant={state === 'invited' ? 'secondary' : 'primary'}
        size={size}
        disabled={!card.actions.invite.allowed}
        onClick={() => {
          setFailure(null);
          setConfirm(true);
        }}
      >
        {label}
      </Button>
      <Dialog
        open={confirm}
        onClose={() => setConfirm(false)}
        width={560}
        title={label}
        subtitle={contactName}
        initialFocus="first"
        footer={
          <>
            <Button variant="secondary" disabled={busy} onClick={() => setConfirm(false)}>
              Abbrechen
            </Button>
            <Button variant="primary" loading={busy} onClick={() => void send()}>
              Einladung senden
            </Button>
          </>
        }
      >
        {failure ? <Banner tone="danger">{failure}</Banner> : null}
        <p className={styles.confirmText}>
          {email
            ? `${contactName} erhält eine E-Mail an ${email} mit dem persönlichen Link zum Mandantenportal. Der Link ist 14 Tage gültig.`
            : `${contactName} erhält eine E-Mail mit dem persönlichen Link zum Mandantenportal. Der Link ist 14 Tage gültig.`}
        </p>
      </Dialog>
    </>
  );
}
